import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Injector,
  OnDestroy,
  OnInit,
  Renderer2,
  Signal,
  inject,
  signal,
  computed,
  input,
  viewChild
} from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { TranslateDirective } from '@ngx-translate/core';
import { ReplaySubject, Subject, Subscription } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { toSignal } from '@angular/core/rxjs-interop';

import { Breakpoint, BreakpointState, MediaQueryService } from '../../../../services/media-query.service';
import { JoyrideStep, StepCounter, StepPosition } from '../../joyride.domain';
import { JoyrideConstants } from '../../joyride.constants';
import { IJoyrideStepService, JoyrideStepService } from '../../services/joyride-step.service';
import { JoyrideStepsContainerService } from '../../services/joyride-steps-container.service';
import { JoyrideEventListenerService } from '../../services/joyride-event-listener.service';
import { JoyrideDocumentService } from '../../services/joyride-document.service';
import { JoyrideOptionsService } from '../../services/joyride-options.service';
import { FocusTrapper, FocusTrapperFactory } from '../../../../components/focus/focus-trapper-factory';
import { DataAnalyticsService } from '../../../../services/data-analytics/data-analytics.service';
import { JoyrideArrowComponent } from '../arrow/joyride-arrow.component';
import { JoyrideButtonComponent } from '../button/joyride-button.component';
import { JoyrideBackButtonComponent } from '../button/joyride-back-button.component';
import { JoyrideCloseButtonComponent } from '../button/joyride-close-button.component';

@Component({
  selector: 'td-wb-joyride-step',
  templateUrl: './joyride-step.component.html',
  styleUrls: ['./joyride-step.component.scss'],
  imports: [AsyncPipe, TranslateDirective, JoyrideArrowComponent, JoyrideButtonComponent, JoyrideBackButtonComponent, JoyrideCloseButtonComponent],
  host: {
    '(touchmove)': 'onTouchMove($event)'
  }
})
export class JoyrideStepComponent implements OnInit, OnDestroy, AfterViewInit {
  private static readonly DEFAULT_DISTANCE_FROM_MARGIN_BOTTOM: number = 5;
  private static readonly DEFAULT_DISTANCE_FROM_MARGIN_RIGHT: number = 5;
  private static readonly DEFAULT_DISTANCE_FROM_MARGIN_TOP: number = 2;
  private static readonly DEFAULT_DISTANCE_FROM_MARGIN_LEFT: number = 2;
  private static readonly STEP_MIN_WIDTH: number = 300;
  private static readonly STEP_MAX_WIDTH: number = 330;
  private static readonly STEP_HEIGHT: number = 200;
  private static readonly ASPECT_RATIO: number = 1.212;

  readonly step = input<JoyrideStep>(undefined);
  readonly stepHolder = viewChild<ElementRef>('stepHolder');
  readonly stepContainer = viewChild<ElementRef>('stepContainer');

  stepWidth: number = JoyrideStepComponent.STEP_MIN_WIDTH;
  stepHeight: number = JoyrideStepComponent.STEP_HEIGHT;
  leftPosition: number;
  topPosition: number;
  showArrow: boolean = true;
  arrowPosition: StepPosition;
  arrowLeftPosition: number;
  arrowTopPosition: number;
  title: ReplaySubject<string>;
  text: ReplaySubject<string>;
  customLabel: ReplaySubject<string>;
  iconSrc: string;
  isCounterVisible: boolean;
  isPrevButtonVisible: boolean;
  standalonePrevButton: boolean;
  isCloseButtonVisible: boolean;
  counter: StepCounter;
  targetHeight: number;
  joyrideStepService: IJoyrideStepService;

  private arrowSize: number = JoyrideConstants.ARROW_SIZE;
  private stepAbsoluteLeft: number;
  private stepAbsoluteTop: number;
  private targetWidth: number;
  private targetAbsoluteLeft: number;
  private targetAbsoluteTop: number;

  private positionAlreadyFixed: boolean;
  private documentHeight: number;
  private focusTrapper: FocusTrapper;
  private readonly destroy: Subject<void> = new Subject<void>();
  private isMobile: Signal<boolean> = signal<boolean>(false);

  private readonly stepsContainerService = inject(JoyrideStepsContainerService);
  private readonly eventListenerService = inject(JoyrideEventListenerService);
  private readonly documentService = inject(JoyrideDocumentService);
  private readonly renderer = inject(Renderer2);
  private readonly optionsService = inject(JoyrideOptionsService);
  private readonly focusTrapperFactory = inject(FocusTrapperFactory);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly dataAnalyticsService = inject(DataAnalyticsService);
  private readonly mediaQueryService = inject(MediaQueryService);
  private readonly injector = inject(Injector);

  constructor() {
    this.isMobile = toSignal(
      this.mediaQueryService.observe(Breakpoint.SM).pipe(
        map((state: BreakpointState) => state.isBreakpointMatch(Breakpoint.SM))
      ),
      { initialValue: false }
    );
  }

  onTouchMove(event: Event): void {
    if (this.step()?.usedAsCoachMark) {
      event.preventDefault();
    }
  }

  ngOnInit(): void {
    // Need to Inject here otherwise you will obtain a circular dependency
    this.joyrideStepService = this.injector.get(JoyrideStepService);

    this.documentHeight = this.documentService.getDocumentHeight();
    this.subscribeToResizeEvents();
    this.title = this.step().title;
    this.text = this.step().text;
    this.customLabel = this.step().customLabel;
    this.iconSrc = this.step().iconSrc;

    this.counter = this.getCounter();
    this.isCounterVisible = this.optionsService.isCounterVisible();
    this.isPrevButtonVisible = this.step().doNotShowPrevButton ? false : this.optionsService.isPrevButtonVisible();
    this.isCloseButtonVisible = this.optionsService.isCloseButtonVisible();
    this.standalonePrevButton = this.optionsService.isPrevButtonStandalone();

    if (this.text) {
      this.text.pipe(takeUntil(this.destroy)).subscribe((val: string) => this.checkRedraw(val));
    }
    if (this.title) {
      this.title.pipe(takeUntil(this.destroy)).subscribe((val: string) => this.checkRedraw(val));
    }
    if (this.customLabel) {
      this.customLabel.pipe(takeUntil(this.destroy)).subscribe((val: string) => this.checkRedraw(val));
    }

    this.focusTrapper = this.focusTrapperFactory.create(this.stepHolder().nativeElement,
      { autoFocus: true });
  }

  getAspectRatio(): number {
    return computed(() => {
      if (this.isMobile()) {
        return JoyrideStepComponent.ASPECT_RATIO;
      }
      return 1;
    })();
  }

  ngAfterViewInit(): void {
    this.setDimensions();
    this.drawStep();
    this.focusTrapper.autoFocusFirstTabbableElement();
  }

  ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.complete();
    this.focusTrapper.returnFocusAfterClose();
    this.focusTrapper.destroy();
  }

  back(): void {
    this.joyrideStepService.back();
    this.focusTrapper.autoFocusFirstTabbableElement();
  }

  next(): void {
    this.joyrideStepService.next();
    this.focusTrapper.autoFocusFirstTabbableElement();
  }

  close(isDone: boolean = false): void {
    if (isDone) {
      this.dataAnalyticsService.emitLinkClickEvent('guidedtour:done');
    }
    this.joyrideStepService.close();
  }

  isLastStep(): boolean {
    return (
      this.stepsContainerService.getStepNumber(this.step().name) ===
      this.stepsContainerService.getStepsCount()
    );
  }

  private checkRedraw(val: unknown): void {
    if (val != null) {
      // Need to wait that the change is rendered before redrawing
      setTimeout(() => {
        this.redrawStep();
      }, 2);
    }
  }

  private drawStep(): void {
    const position = this.step().isElementOrAncestorFixed
      ? 'fixed'
      : 'absolute';
    const stepHolder = this.stepHolder();
    this.renderer.setStyle(
      stepHolder.nativeElement,
      'position',
      position
    );
    const step = this.step();
    this.renderer.setStyle(
      stepHolder.nativeElement,
      'transform',
      step.transformCssStyle
    );
    this.targetWidth = step.targetViewContainer.element.nativeElement.getBoundingClientRect().width;
    this.targetHeight = step.targetViewContainer.element.nativeElement.getBoundingClientRect().height;
    this.targetAbsoluteLeft =
      position === 'fixed'
        ? this.documentService.getElementFixedLeft(
          step.targetViewContainer.element
        )
        : this.documentService.getElementAbsoluteLeft(
          step.targetViewContainer.element
        );
    this.targetAbsoluteTop =
      position === 'fixed'
        ? this.documentService.getElementFixedTop(
          step.targetViewContainer.element
        )
        : this.documentService.getElementAbsoluteTop(
          step.targetViewContainer.element
        );
    this.setStepStyle();
  }

  private getCounter(): StepCounter {
    const stepPosition = this.stepsContainerService.getStepNumber(
      this.step().name
    );
    const numberOfSteps = this.stepsContainerService.getStepsCount();
    return { step: stepPosition, total: numberOfSteps };
  }

  private setStepStyle(): void {
    switch (this.step().position) {
      case 'top': {
        this.setStyleTop();
        break;
      }
      case 'bottom': {
        this.setStyleBottom();
        break;
      }
      case 'right': {
        this.setStyleRight();
        break;
      }
      case 'left': {
        this.setStyleLeft();
        break;
      }
      case 'center': {
        this.setStyleCenter();
        break;
      }
      default: {
        this.setStyleBottom();
      }
    }
  }

  private setStyleTop(): void {
    this.stepsContainerService.updatePosition(this.step().name, 'top');
    this.topPosition =
      this.targetAbsoluteTop - JoyrideConstants.DISTANCE_FROM_TARGET - this.stepHeight;
    this.stepAbsoluteTop =
      this.targetAbsoluteTop - JoyrideConstants.DISTANCE_FROM_TARGET - this.stepHeight;
    this.arrowTopPosition = this.stepHeight;

    this.leftPosition =
      this.targetWidth / 2 - this.stepWidth / 2 + this.targetAbsoluteLeft;
    this.stepAbsoluteLeft =
      this.targetWidth / 2 - this.stepWidth / 2 + this.targetAbsoluteLeft;
    this.arrowLeftPosition = this.stepWidth / 2 - this.arrowSize;

    this.adjustLeftPosition();
    this.adjustRightPosition();
    this.arrowPosition = StepPosition.BOTTOM;
    this.autofixTopPosition();
  }

  private setStyleRight(): void {
    this.stepsContainerService.updatePosition(this.step().name, 'right');
    this.topPosition =
      this.targetAbsoluteTop +
      this.targetHeight / 2 -
      this.stepHeight / 2;
    this.stepAbsoluteTop =
      this.targetAbsoluteTop +
      this.targetHeight / 2 -
      this.stepHeight / 2;
    this.arrowTopPosition = this.stepHeight / 2 - this.arrowSize;

    this.leftPosition =
      this.targetAbsoluteLeft + this.targetWidth + JoyrideConstants.DISTANCE_FROM_TARGET;
    this.stepAbsoluteLeft =
      this.targetAbsoluteLeft + this.targetWidth + JoyrideConstants.DISTANCE_FROM_TARGET;
    this.arrowLeftPosition = -this.arrowSize;
    this.adjustTopPosition();
    this.adjustBottomPosition();
    this.arrowPosition = StepPosition.LEFT;
    this.autofixRightPosition();
  }

  private setStyleBottom(): void {
    this.stepsContainerService.updatePosition(this.step().name, 'bottom');
    this.topPosition =
      this.targetAbsoluteTop + this.targetHeight + JoyrideConstants.DISTANCE_FROM_TARGET;
    this.stepAbsoluteTop =
      this.targetAbsoluteTop + this.targetHeight + JoyrideConstants.DISTANCE_FROM_TARGET;
    this.arrowTopPosition = -this.arrowSize;

    this.arrowLeftPosition = this.stepWidth / 2 - this.arrowSize;
    this.leftPosition =
      this.targetWidth / 2 - this.stepWidth / 2 + this.targetAbsoluteLeft;
    this.stepAbsoluteLeft =
      this.targetWidth / 2 - this.stepWidth / 2 + this.targetAbsoluteLeft;
    this.adjustLeftPosition();
    this.adjustRightPosition();
    this.arrowPosition = StepPosition.TOP;
    this.autofixBottomPosition();
  }

  private setStyleLeft(): void {
    this.stepsContainerService.updatePosition(this.step().name, 'left');
    this.topPosition =
      this.targetAbsoluteTop +
      this.targetHeight / 2 -
      this.stepHeight / 2;
    this.stepAbsoluteTop =
      this.targetAbsoluteTop +
      this.targetHeight / 2 -
      this.stepHeight / 2;
    this.arrowTopPosition = this.stepHeight / 2 - this.arrowSize;

    this.leftPosition =
      this.targetAbsoluteLeft - this.stepWidth - JoyrideConstants.DISTANCE_FROM_TARGET;
    this.stepAbsoluteLeft =
      this.targetAbsoluteLeft - this.stepWidth - JoyrideConstants.DISTANCE_FROM_TARGET;
    this.arrowLeftPosition = this.stepWidth;
    this.adjustTopPosition();
    this.adjustBottomPosition();
    this.arrowPosition = StepPosition.RIGHT;
    this.autofixLeftPosition();
  }

  private setStyleCenter(): void {
    const stepHolder = this.stepHolder();
    this.renderer.setStyle(
      stepHolder.nativeElement,
      'position',
      'fixed'
    );
    this.renderer.setStyle(stepHolder.nativeElement, 'top', '50%');
    this.renderer.setStyle(stepHolder.nativeElement, 'left', '50%');

    this.updateStepDimensions();

    this.renderer.setStyle(
      stepHolder.nativeElement,
      'transform',
      `translate(-${this.stepWidth / 2}px, -${this.stepHeight / 2}px)`
    );
    this.showArrow = false;
  }

  private adjustLeftPosition(): void {
    if (this.leftPosition < 0) {
      this.arrowLeftPosition =
        this.arrowLeftPosition +
        this.leftPosition -
        JoyrideStepComponent.DEFAULT_DISTANCE_FROM_MARGIN_LEFT;
      this.leftPosition = JoyrideStepComponent.DEFAULT_DISTANCE_FROM_MARGIN_LEFT;
    }
  }

  private adjustRightPosition(): void {
    const currentWindowWidth = document.body.clientWidth;
    if (this.stepAbsoluteLeft + this.stepWidth > currentWindowWidth) {
      const newLeftPos =
        this.leftPosition -
        (this.stepAbsoluteLeft +
          this.stepWidth +
          JoyrideStepComponent.DEFAULT_DISTANCE_FROM_MARGIN_RIGHT -
          currentWindowWidth);
      const deltaLeftPosition = newLeftPos - this.leftPosition;

      this.leftPosition = newLeftPos;
      this.arrowLeftPosition = this.arrowLeftPosition - deltaLeftPosition;
    }
  }

  private adjustTopPosition(): void {
    if (this.stepAbsoluteTop < 0) {
      this.arrowTopPosition =
        this.arrowTopPosition +
        this.topPosition -
        JoyrideStepComponent.DEFAULT_DISTANCE_FROM_MARGIN_TOP;
      this.topPosition = JoyrideStepComponent.DEFAULT_DISTANCE_FROM_MARGIN_TOP;
    }
  }

  private adjustBottomPosition(): void {
    if (this.stepAbsoluteTop + this.stepHeight > this.documentHeight) {
      const newTopPos =
        this.topPosition -
        (this.stepAbsoluteTop +
          this.stepHeight +
          JoyrideStepComponent.DEFAULT_DISTANCE_FROM_MARGIN_BOTTOM -
          this.documentHeight);
      const deltaTopPosition = newTopPos - this.topPosition;

      this.topPosition = newTopPos;
      this.arrowTopPosition = this.arrowTopPosition - deltaTopPosition;
    }
  }

  private autofixTopPosition(): void {
    if (this.positionAlreadyFixed) {
      // 'No step positions found for this step. The step will be centered.'
    } else if (
      this.targetAbsoluteTop - this.stepHeight - this.arrowSize <
      0
    ) {
      this.positionAlreadyFixed = true;
      this.setStyleRight();
    }
  }

  private autofixRightPosition(): void {
    if (
      this.targetAbsoluteLeft +
      this.targetWidth +
      this.stepWidth +
      this.arrowSize >
      document.body.clientWidth
    ) {
      this.setStyleBottom();
    }
  }

  private autofixBottomPosition(): void {
    if (
      this.targetAbsoluteTop +
      this.stepHeight +
      this.arrowSize +
      this.targetHeight >
      this.documentHeight
    ) {
      this.setStyleLeft();
    }
  }

  private autofixLeftPosition(): void {
    if (this.targetAbsoluteLeft - this.stepWidth - this.arrowSize < 0) {
      this.setStyleTop();
    }
  }

  private subscribeToResizeEvents(): Subscription {
    return this.eventListenerService.resizeEvent
      .pipe(takeUntil(this.destroy))
      .subscribe(() => {
        if (!this.step().usedAsCoachMark) {
          this.redrawStep();
        }
      });
  }

  redrawStep(): void {
    this.updateStepDimensions();
    this.drawStep();
    this.cdr.detectChanges();
  }

  private getDimensionsByAspectRatio(
    width: number,
    height: number,
    aspectRatio: number
  ): { width: number; height: number } {
    const calcHeight = (width + height) / (1 + aspectRatio);
    const calcWidth = calcHeight * aspectRatio;
    return {
      width: calcWidth,
      height: this.step().usedAsCoachMark ? height : calcHeight
    };
  }

  private adjustDimensions(width: number, height: number): { width: number; height: number } {
    const area = width * height;
    let newWidth = width;
    let newHeight = height;
    if (width > JoyrideStepComponent.STEP_MAX_WIDTH) {
      newWidth = JoyrideStepComponent.STEP_MAX_WIDTH;
      newHeight = area / newWidth;
    } else if (width < JoyrideStepComponent.STEP_MIN_WIDTH) {
      newWidth = JoyrideStepComponent.STEP_MIN_WIDTH;
      newHeight = JoyrideStepComponent.STEP_MIN_WIDTH / this.getAspectRatio();
    }
    return {
      width: newWidth,
      height: this.step().usedAsCoachMark ? height : newHeight
    };
  }

  private updateStepDimensions(): void {
    this.stepWidth = this.stepContainer().nativeElement.clientWidth;
    this.stepHeight = this.stepContainer().nativeElement.clientHeight;
  }

  private setDimensions(): void {
    const stepContainer = this.stepContainer();
    this.renderer.setStyle(
      stepContainer.nativeElement,
      'min-width',
      'unset'
    );
    this.renderer.setStyle(
      stepContainer.nativeElement,
      'max-width',
      JoyrideStepComponent.STEP_MAX_WIDTH + 'px'
    );

    let dimensions = this.getDimensionsByAspectRatio(
      stepContainer.nativeElement.clientWidth,
      stepContainer.nativeElement.clientHeight,
      this.getAspectRatio()
    );
    dimensions = this.adjustDimensions(
      dimensions.width,
      dimensions.height
    );
    this.stepWidth = dimensions.width;
    this.stepHeight = dimensions.height;
    this.renderer.setStyle(
      stepContainer.nativeElement,
      'width',
      this.stepWidth + 'px'
    );

    if (this.step().usedAsCoachMark) {
      dimensions = this.getDimensionsByAspectRatio(
        stepContainer.nativeElement.clientWidth,
        stepContainer.nativeElement.clientHeight,
        this.getAspectRatio()
      );
      this.stepHeight = dimensions.height;
    }

    this.renderer.setStyle(
      stepContainer.nativeElement,
      'height',
      this.stepHeight + 'px'
    );
  }
}
